/**
 * Next steps: what the latest recorded Auto-Investigate run suggests the officer
 * does next (GET /cases/{id}/runs/{run_id}/next-steps, owner decision OD10).
 *
 * Each suggestion shows its reason (index-keyed: seed#1, cp#2, target#1), its
 * cost class and an honest origin label: "Rule" when no model was involved,
 * "Rule, ordered by model" only when a model ranking was applied to the run.
 * Run starts the step as a case-worker job and hands the job to the page's
 * existing polling (`onJobStarted`); Dismiss records the officer's reason. A
 * collapsible log lists what ran automatically, each with its reason.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Compass, Loader2, Play, RefreshCw, X } from 'lucide-react';
import {
    caseDirectionService,
    type CanvasJobEnvelope,
    type CaseRun,
    type NextStep,
    type NextStepsResponse,
} from '../../services/api';
import { runErrorMessage, shortId } from './caseRuns';

export interface NextStepsPanelProps {
    caseId: string;
    /** The case's runs as the page loaded them (newest first). */
    runs: CaseRun[];
    /** Changes whenever the page refreshes its runs list. */
    refreshKey?: number;
    /** A case-worker run is going for this case: Run is disabled (one job per case). */
    runBusy?: boolean;
    /** Hand a started job to the page's job polling. */
    onJobStarted: (job: CanvasJobEnvelope) => void;
}

const COST_LABEL: Record<string, { label: string; tone: string; hint: string }> = {
    read_only: { label: 'Read only', tone: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
                 hint: 'Works on data already in the run; no outside call' },
    fetch: { label: 'Fetches data', tone: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30',
             hint: 'Calls blockchain data providers' },
    paid: { label: 'Paid lookup', tone: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
            hint: 'Uses a paid or quota-limited provider' },
};

export function originLabel(step: Pick<NextStep, 'origin'>): string {
    return step.origin === 'model' || step.origin === 'model_rank' ? 'Rule, ordered by model' : 'Rule';
}

export function methodLabel(data: Pick<NextStepsResponse, 'method' | 'no_model_call' | 'generation_id' | 'blocked_calls'>): string {
    const blocked = data.blocked_calls ? ` · ${data.blocked_calls} model call withheld by the data policy` : '';
    if (data.method === 'llm') {
        return `Rule-based, ordered by a model${data.generation_id ? ` (generation ${shortId(data.generation_id)})` : ''}${blocked}`;
    }
    if (data.method === 'llm_failed_deterministic') {
        return `Rule-based; the model ordering failed, rule order kept${blocked}`;
    }
    return `Rule-based${data.no_model_call === false ? '' : ' (no model call)'}${blocked}`;
}

/** The newest run that has a recorded event (only recorded runs carry next steps). */
export function latestRecordedRun(runs: CaseRun[]): CaseRun | null {
    return runs.find((r) => Boolean(r.event_id)) ?? null;
}

function statusText(step: NextStep): string | null {
    if (step.status === 'executed') {
        if (step.executed_by === 'autorun') return 'Ran automatically';
        return step.job_id ? `Started by an officer (job ${shortId(step.job_id)})` : 'Started by an officer';
    }
    if (step.status === 'dismissed') return `Dismissed${step.dismissed_reason ? `: ${step.dismissed_reason}` : ''}`;
    return null;
}

interface RowProps {
    step: NextStep;
    runBusy: boolean;
    pending: boolean;
    onRun: (s: NextStep) => void;
    onDismiss: (s: NextStep, reason: string) => void;
}

function StepRow({ step, runBusy, pending, onRun, onDismiss }: RowProps) {
    const [dismissing, setDismissing] = useState(false);
    const [reason, setReason] = useState('');
    const cost = COST_LABEL[step.cost_class] ?? { label: step.cost_class, tone: 'bg-secondary text-muted-foreground border-border', hint: '' };
    const offered = step.status === 'offered';
    const done = statusText(step);

    return (
        <li className="px-4 py-3" data-testid="next-step-row" data-step-status={step.status} data-pass-id={step.pass_id}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{step.title || step.pass_id}</span>
                <span data-testid="next-step-cost" title={cost.hint}
                    className={`text-[10px] px-1.5 py-0.5 rounded border ${cost.tone}`}>{cost.label}</span>
                <span data-testid="next-step-origin"
                    className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground"
                    title={step.origin === 'model' || step.origin === 'model_rank' ? 'The step comes from a rule; a model only changed its position in the list' : 'Built by a rule from the run results; no model involved'}>
                    {originLabel(step)}
                </span>
                {!step.runnable && (
                    <span data-testid="next-step-manual" className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground">
                        Manual step
                    </span>
                )}
                <div className="ml-auto flex items-center gap-1.5">
                    {offered && step.runnable && (
                        <button type="button" data-testid="next-step-run"
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:bg-secondary disabled:opacity-50"
                            disabled={runBusy || pending}
                            title={runBusy ? 'A case-worker run is already going for this case' : 'Start this step as a case-worker run'}
                            onClick={() => onRun(step)}>
                            {pending ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden /> : <Play className="w-3 h-3" aria-hidden />}
                            Run
                        </button>
                    )}
                    {offered && !dismissing && (
                        <button type="button" data-testid="next-step-dismiss"
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:bg-secondary disabled:opacity-50"
                            disabled={pending} onClick={() => setDismissing(true)}>
                            <X className="w-3 h-3" aria-hidden /> Dismiss
                        </button>
                    )}
                </div>
            </div>
            <p className="text-sm text-muted-foreground mt-1" data-testid="next-step-reason">{step.reason}</p>
            {step.model_note && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="next-step-model-note">
                    <span className="font-medium">Model note:</span> {step.model_note}
                </p>
            )}
            {step.inputs.length > 0 && (
                <p className="text-[11px] text-muted-foreground mt-1">Inputs: {step.inputs.join(', ')}</p>
            )}
            {done && <p className="text-xs mt-1" data-testid="next-step-status">{done}</p>}
            {dismissing && (
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="next-step-dismiss-form">
                    <input aria-label="Reason for dismissing" data-testid="next-step-dismiss-reason"
                        className="flex-1 min-w-[12rem] text-xs px-2 py-1 rounded border border-border bg-background"
                        maxLength={500} value={reason} placeholder="Why is this step not needed?"
                        onChange={(e) => setReason(e.target.value)} />
                    <button type="button" data-testid="next-step-dismiss-confirm"
                        className="text-xs px-2 py-1 rounded border border-border hover:bg-secondary disabled:opacity-50"
                        disabled={!reason.trim() || pending}
                        onClick={() => { onDismiss(step, reason.trim()); setDismissing(false); setReason(''); }}>
                        Dismiss step
                    </button>
                    <button type="button" className="text-xs px-2 py-1" onClick={() => { setDismissing(false); setReason(''); }}>
                        Cancel
                    </button>
                </div>
            )}
        </li>
    );
}

export function NextStepsPanel({ caseId, runs, refreshKey = 0, runBusy = false, onJobStarted }: NextStepsPanelProps) {
    const run = useMemo(() => latestRecordedRun(runs), [runs]);
    const runRef = run?.event_id || null;
    const [data, setData] = useState<NextStepsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [pendingId, setPendingId] = useState<string | null>(null);
    const [logOpen, setLogOpen] = useState(false);
    const [nonce, setNonce] = useState(0);

    // Another run (or case): forget the previous run's steps and messages.
    useEffect(() => {
        setData(null);
        setError(null);
        setNotice(null);
    }, [caseId, runRef]);

    useEffect(() => {
        if (!caseId || !runRef) return;
        let cancelled = false;   // R8: an answer for another run never lands
        setLoading(true);
        caseDirectionService.get(caseId, runRef)
            .then((res) => { if (!cancelled) { setData(res); setError(null); } })
            .catch((err: unknown) => {
                if (cancelled) return;
                const status = (err as { response?: { status?: number } })?.response?.status;
                setError(status === 404 ? 'This run has no recorded next steps.' : `Failed to load next steps: ${runErrorMessage(err)}`);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [caseId, runRef, refreshKey, nonce]);

    const reload = useCallback(() => setNonce((n) => n + 1), []);

    const handleRun = useCallback(async (step: NextStep) => {
        if (!runRef) return;
        setPendingId(step.suggestion_id);
        setNotice(null);
        try {
            const job = await caseDirectionService.run(caseId, runRef, step.suggestion_id);
            setNotice(`${step.title || step.pass_id} started as a case-worker run. Progress shows under Runs.`);
            onJobStarted(job);
            reload();
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            setNotice(status === 409
                ? `Not started: ${runErrorMessage(err)}`
                : `Could not start the step: ${runErrorMessage(err)}`);
        } finally {
            setPendingId(null);
        }
    }, [caseId, runRef, onJobStarted, reload]);

    const handleDismiss = useCallback(async (step: NextStep, reason: string) => {
        if (!runRef) return;
        setPendingId(step.suggestion_id);
        setNotice(null);
        try {
            const updated = await caseDirectionService.dismiss(caseId, runRef, step.suggestion_id, reason);
            setData((cur) => cur ? {
                ...cur,
                suggestions: cur.suggestions.map((s) => (s.suggestion_id === updated.suggestion_id ? { ...s, ...updated } : s)),
            } : cur);
        } catch (err: unknown) {
            setNotice(`Could not dismiss the step: ${runErrorMessage(err)}`);
        } finally {
            setPendingId(null);
        }
    }, [caseId, runRef]);

    const suggestions = data?.suggestions ?? [];
    const offered = suggestions.filter((s) => s.status === 'offered');
    const others = suggestions.filter((s) => s.status !== 'offered');
    const autoLog = data?.autorun_log ?? [];
    const autorunOn = Boolean(data?.autorun?.enabled);

    return (
        <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="next-steps-panel" data-tour="case-next-steps">
            <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <Compass className="w-5 h-5 text-primary" aria-hidden />
                    <h3 className="font-medium">Next steps</h3>
                    {data && <span className="px-2 py-0.5 text-xs bg-secondary rounded-full" data-testid="next-steps-count">{offered.length}</span>}
                </div>
                <button type="button" onClick={reload} disabled={!runRef || loading}
                    className="p-1.5 rounded hover:bg-secondary disabled:opacity-50" aria-label="Refresh next steps">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
                </button>
            </div>
            {!runRef ? (
                <p className="p-4 text-sm text-muted-foreground" data-testid="next-steps-empty">
                    Suggestions appear here after an Auto-Investigate run has been recorded.
                </p>
            ) : error ? (
                <p className="p-4 text-sm text-muted-foreground" data-testid="next-steps-error">{error}</p>
            ) : !data ? (
                <p className="p-4 text-sm text-muted-foreground">Loading next steps…</p>
            ) : (
                <>
                    <div className="px-4 pt-3 text-xs text-muted-foreground space-y-0.5">
                        <p data-testid="next-steps-method">{methodLabel(data)}</p>
                        <p data-testid="next-steps-autorun">
                            Automatic runs: {autorunOn
                                ? 'on for read-only steps in this deep run'
                                : data.autorun?.setting && !data.autorun?.eligible_run
                                    ? 'not used (not a deep run)'
                                    : 'off; every step waits for an officer'}
                            {' '}· Run {shortId(data.run_id || data.event_id)}
                        </p>
                    </div>
                    {notice && <p className="px-4 pt-2 text-xs" data-testid="next-steps-notice">{notice}</p>}
                    {!data.recorded ? (
                        <p className="p-4 text-sm text-muted-foreground">This run was recorded before next steps existed.</p>
                    ) : suggestions.length === 0 ? (
                        <p className="p-4 text-sm text-muted-foreground">No further steps suggested for this run.</p>
                    ) : (
                        <ul className="divide-y divide-border">
                            {[...offered, ...others].map((s) => (
                                <StepRow key={s.suggestion_id} step={s} runBusy={runBusy}
                                    pending={pendingId === s.suggestion_id} onRun={handleRun} onDismiss={handleDismiss} />
                            ))}
                        </ul>
                    )}
                    <div className="border-t border-border">
                        <button type="button" data-testid="next-steps-log-toggle"
                            className="w-full flex items-center gap-1 px-4 py-2 text-xs text-muted-foreground hover:bg-secondary/30"
                            aria-expanded={logOpen} onClick={() => setLogOpen((o) => !o)}>
                            {logOpen ? <ChevronDown className="w-3 h-3" aria-hidden /> : <ChevronRight className="w-3 h-3" aria-hidden />}
                            Ran automatically ({autoLog.length})
                        </button>
                        {logOpen && (
                            autoLog.length === 0 ? (
                                <p className="px-4 pb-3 text-xs text-muted-foreground" data-testid="next-steps-log-empty">
                                    Nothing ran automatically in this run.
                                </p>
                            ) : (
                                <ul className="px-4 pb-3 space-y-1" data-testid="next-steps-log">
                                    {autoLog.map((e, i) => (
                                        <li key={`${e.suggestion_id || e.pass_id}-${i}`} className="text-xs" data-testid="next-steps-log-entry">
                                            <span className="font-medium">{e.pass_id || (e.selected || []).join(', ')}</span>
                                            {e.produced === false ? ' (no output)' : ''}: {e.reason}
                                        </li>
                                    ))}
                                </ul>
                            )
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default NextStepsPanel;
